import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { SCORE_LEVEL_COLORS } from '../../../../constants/analysis';
import { getCoverageSegments } from '../../../../utils/client-insights';
import type { AnalysisScores, CoverageKey, CoverageSegment } from '../../../../utils/client-insights';
import type { IntlLike } from '../clientPresentation';

// v46 保障缺口雷達 (handoff PART 6b): a hand-rolled SVG radar, deliberately NOT
// Chart.js/OverallRadar. Chart.js needs a real <canvas> the jsdom test env does
// not provide, which would break the drawer's whole test file; an SVG polygon is
// plain DOM, renders identically under jsdom, and matches the preview's own
// mock radar 1:1. Five axes, matching the OLD OverallRadar (handoff 6g item 1):
// 危疾/儲蓄/醫療/人壽/排名 (peerScore) - NOT the invented 「投資」 the first
// redesign pass had.

// Axis order and placement match V1's OverallRadar exactly. Chart.js renders its
// `labels` array clockwise from the top, so V1's [C.I., Saving, Med, Peer, Life]
// puts 排名 at lower-left and 人壽 at upper-left. -90° is the top; 72° steps
// clockwise from there reproduce that, so an adviser reading both pages finds
// each axis in the same place.
const AXES: { key: CoverageKey; scoreKey: keyof AnalysisScores; angle: number; labelKey: string }[] = [
  { key: 'ci', scoreKey: 'ciScore', angle: -90, labelKey: 'clientv2-axis-ci' },
  { key: 'saving', scoreKey: 'savingScore', angle: -18, labelKey: 'clientv2-axis-saving' },
  { key: 'medical', scoreKey: 'medicalScore', angle: 54, labelKey: 'clientv2-axis-medical' },
  { key: 'peer', scoreKey: 'peerScore', angle: 126, labelKey: 'clientv2-axis-peer' },
  { key: 'life', scoreKey: 'lifeScore', angle: 198, labelKey: 'clientv2-axis-life' }
];

// Box and radius, measured rather than guessed — `svg.getBBox()` on the live
// chart, at BOTH label font sizes (14 desktop / 18 phone) and in both
// languages, since 「儲蓄 ⚠」 and 'Saving ⚠' are the widest thing on the widest
// axis and they decide how much room is left for the pentagon.
//
// What the old numbers wasted: with CY at the box's own middle the drawing sat
// high in it — 2 units of clearance above the top label against 24 below the
// bottom pair. The chart is width-constrained (the svg is `width: 100%`, so its
// height follows the ratio), and that idle 24 units at the foot was making the
// box taller for nothing while the radius stayed small.
//
// So the drawing is centred on its own ink instead, the box is cropped to what
// the ink actually needs, and the radius takes the room that frees. Horizontal
// is the binding side and has almost no slack at the phone font — R is at its
// limit here. Re-measure before changing R, CY, the label gap, or any axis
// label text; the labels are the constraint, not the polygon.
const CX = 140;
const CY = 120;
const R = 88;

// Ring-to-label gap. Has to clear the text's own half-height so a label never
// sits on the outer ring it names.
const LABEL_GAP = 16;

/**
 * MIRROR — `getScoreColor` / `getScoreEmoji` in utils/analysis.js, the pair V1's
 * OverallRadar colours its axis labels with. Mirrored rather than imported:
 * `utils/analysis` pulls `store/analysis/duck` -> `store/index` -> `utils/native`
 * -> `@capacitor/preferences`, whose ESM breaks jest's CJS transform and takes
 * this component's whole test file down with it (Established Pattern #1).
 * The bands come from the SAME `SCORE_LEVEL_COLORS` constant, which is an
 * import-free leaf, so only the four thresholds are restated here.
 *
 * One deliberate difference from V1: V1 passes a missing score straight through,
 * and `getScoreColor(undefined)` falls to the final `else` and paints it #ff0000
 * with a ⚠ — a client who has simply never been analysed reads as a critical
 * gap. A missing score is *unknown*, not a gap, so it keeps the neutral grey and
 * no ⚠ here. Every client who HAS a score renders identically to V1.
 */
const UNKNOWN_LABEL_COLOR = '#64748b';

const scoreColorOf = (score: number): string => {
  if (score >= 75) return SCORE_LEVEL_COLORS[3];
  if (score >= 50) return SCORE_LEVEL_COLORS[2];
  if (score >= 25) return SCORE_LEVEL_COLORS[1];
  return SCORE_LEVEL_COLORS[0];
};

const scoreEmojiOf = (score: number): string => (score >= 50 ? '' : ' ⚠ ');

const axisScoreOf = (scores: AnalysisScores, scoreKey: keyof AnalysisScores): number | null => {
  const raw = scores[scoreKey];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
};

type SegmentState = 'covered' | 'gap' | 'unknown';

// Same three states CoverageMeter uses, so the radar and the row meter tell one
// story. Threshold 75 matches getScoreColor in utils/analysis.js.
const stateOf = (segment: CoverageSegment): SegmentState =>
  segment.score === null ? 'unknown' : segment.covered ? 'covered' : 'gap';

const pointAt = (angleDeg: number, scale: number): { x: number; y: number; str: string } => {
  const rad = (angleDeg * Math.PI) / 180;
  const r = R * scale;
  const x = CX + r * Math.cos(rad);
  const y = CY + r * Math.sin(rad);
  return { x, y, str: `${x},${y}` };
};

const ringPoints = (scale: number): string => AXES.map(axis => pointAt(axis.angle, scale).str).join(' ');

const pathToD = (pts: { x: number; y: number }[]): string =>
  pts.length === 0
    ? ''
    : `M ${pts[0].x} ${pts[0].y} ` +
      pts
        .slice(1)
        .map(p => `L ${p.x} ${p.y}`)
        .join(' ') +
      ' Z';

const ZERO_PATH_D = pathToD(AXES.map(axis => pointAt(axis.angle, 0.02)));
const ZERO_POINTS = ringPoints(0.02);

export type CoverageRadarProps = {
  scores: AnalysisScores;
  intl: IntlLike;
};

// Shared with the aria-label so a screen reader gets the same "glance" a sighted
// user gets from the polygon, rather than five unlabelled <polygon> nodes.
export const describeCoverage = (segments: CoverageSegment[]): string =>
  `Coverage: ${segments.map(segment => `${segment.key} ${stateOf(segment)}`).join(', ')}`;

const CoverageRadar = (props: CoverageRadarProps) => {
  const { scores, intl } = props;
  const segments = getCoverageSegments(scores);

  // Compute exact 0-100 score radius scale for each axis, exactly matching old UI
  // OverallRadar. The score rides along because the outline and the dots want
  // different things from it: the outline needs a vertex on every axis to stay a
  // pentagon, so an unscored one takes the 0.05 placeholder; the dots must NOT
  // mark that placeholder — see below.
  const axisPlots = AXES.map(axis => {
    const score = axisScoreOf(scores, axis.scoreKey);
    const scale = score !== null ? Math.min(1, Math.max(0, score / 100)) : 0.05;
    return { axis, score, point: pointAt(axis.angle, scale) };
  });

  const axisPoints = axisPlots.map(plot => plot.point);
  const targetPathD = pathToD(axisPoints);
  const targetPoints = axisPoints.map(p => p.str).join(' ');

  const [pathD, setPathD] = useState(ZERO_PATH_D);
  const [points, setPoints] = useState(ZERO_POINTS);
  const [active, setActive] = useState(false);

  useEffect(() => {
    setActive(false);
    setPathD(ZERO_PATH_D);
    setPoints(ZERO_POINTS);
    const timer = requestAnimationFrame(() => {
      setPathD(targetPathD);
      setPoints(targetPoints);
      setActive(true);
    });
    return () => cancelAnimationFrame(timer);
  }, [targetPathD, targetPoints]);

  return (
    <div className="clientv2-radar">
      {/* 現有保障 is the chart's heading, and takes 家庭 and 標籤's own heading
          class so the two columns of this row start on one line, in one voice.
          It used to be a blue dot and 11px grey — a colour key, which on a
          chart carrying exactly ONE series told the reader nothing the only
          coloured shape on it did not already say, while reading as a caption
          that had drifted off the pentagon. The shield says 「保障」 the way
          the house says 「家庭」. */}
      <div className="clientv2-subsect clientv2-radar-legend">
        <ShieldCheck size={15} aria-hidden="true" />
        {intl.formatMessage({ id: 'clientv2-radar-legend' })}
      </div>
      <svg
        viewBox="0 0 280 216"
        width="100%"
        role="img"
        aria-label={describeCoverage(segments)}
        data-testid="coverage-radar"
      >
        {[0.33, 0.66, 1].map(scale => (
          <polygon key={scale} points={ringPoints(scale)} fill="none" stroke="#e2e8f0" />
        ))}
        {/* Spokes, centre out to each outer vertex. The rings alone gave the
            eye nothing to fix the centre on, so a lopsided client — two axes
            near zero collapsed onto the middle, three reaching half way —
            read as a polygon that had slipped off-centre rather than as the
            shape of the data. Every axis now shows even when its own value
            collapses to nothing, and the point they all meet at IS the
            centre, which is the thing that was missing. Lighter than the
            rings: they are the anchor, not another gridline to read. */}
        {AXES.map(axis => {
          const outer = pointAt(axis.angle, 1);
          return <line key={axis.key} x1={CX} y1={CY} x2={outer.x} y2={outer.y} stroke="#eef2f7" />;
        })}
        <path
          className={`clientv2-radar-area ${active ? 'is-active' : 'is-mounting'}`}
          d={pathD}
          fill="rgba(57,102,248,.18)"
          stroke="#3966f8"
          strokeWidth={2}
        />
        <polygon
          className={`clientv2-radar-area ${active ? 'is-active' : 'is-mounting'}`}
          points={points}
          fill="none"
          stroke="none"
        />
        {/* One dot per plotted point, V1's own marking (OverallRadar's
            pointRadius). It answers the question the outline cannot: an axis
            scoring 5 puts its vertex under 3px from the centre, so the polygon
            pinches shut there and reads as a shape that has come adrift from
            the middle — when what it actually means is 「this score is nearly
            zero」. A dot sitting on the centre says that; a corner does not.
            Grouped so the mount animation scales the dots with the outline
            rather than leaving them behind.

            An axis with NO score gets no dot. Its vertex is the 0.05
            placeholder, not a measurement, and a solid marker on it is a claim
            the data does not support — 「nearly zero」 where the truth is
            「nobody has run this」. The radar renders as soon as ONE axis has a
            score (hasCoverageData is a some()), so a client with a policy and an
            empty factsheet reaches this with four unscored axes; dotting them
            would have contradicted the grey, ⚠-less label those same axes get
            from UNKNOWN_LABEL_COLOR. The outline still passes through them,
            because a four-sided 「pentagon」 would misread far worse. */}
        <g className={`clientv2-radar-area ${active ? 'is-active' : 'is-mounting'}`}>
          {axisPlots
            .filter(plot => plot.score !== null)
            .map(plot => (
              <circle
                key={plot.axis.key}
                cx={plot.point.x}
                cy={plot.point.y}
                r={2.6}
                fill="#3966f8"
                data-testid="coverage-point"
                data-axis={plot.axis.key}
              />
            ))}
        </g>
        {AXES.map((axis, index) => {
          const rad = (axis.angle * Math.PI) / 180;
          const labelR = R + LABEL_GAP;
          const scoreVal = axisScoreOf(scores, axis.scoreKey);
          return (
            <text
              key={axis.key}
              x={CX + labelR * Math.cos(rad)}
              y={CY + labelR * Math.sin(rad)}
              // Size lives in CSS (`.clientv2-radar text`), not here: the svg
              // is `width: 100%`, so the on-screen size depends on how wide a
              // column it lands in, and that is a breakpoint decision.
              fill={scoreVal === null ? UNKNOWN_LABEL_COLOR : scoreColorOf(scoreVal)}
              textAnchor="middle"
              dominantBaseline="middle"
              data-testid="coverage-axis-label"
              // The shortfall now lives on the WORD, not on a line drawn out to
              // the ring. Those runs animated themselves into the chart after
              // the pentagon had settled, which is two motions for one fact and
              // put five red strokes across a 280-unit box. The ⚠ was already
              // here saying the same thing; this just makes it move.
              //
              // Marked rather than styled inline: the pulse is a keyframe, and a
              // keyframe on an `animation` shorthand cannot be expressed in a
              // style attribute the way a colour can.
              className={scoreVal !== null && scoreEmojiOf(scoreVal) ? 'clientv2-radar-warn' : undefined}
              // Staggered so the three that are short do not beat as one block —
              // read as three separate answers, which is what they are.
              style={{ '--warn-delay': `${index * 260}ms` } as React.CSSProperties}
            >
              {intl.formatMessage({ id: axis.labelKey })}
              {scoreVal === null ? '' : scoreEmojiOf(scoreVal)}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export default CoverageRadar;
